import { BlackPopupRolagem } from "@/components/blog/BlackPopupRolagem";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/feo/SiteHeader";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import { SiteFooter } from "@/components/feo/SiteFooter";
import { PROFESSORES_BY_PRODUCT } from "@/components/posfigma/professores-by-product";
import qs1 from "@/assets/feo/qs-1.webp";
import qs2 from "@/assets/feo/qs-2.webp";
import helio from "@/assets/feo/helio.webp";
import leandro from "@/assets/feo/leandro.webp";
import lucas from "@/assets/feo/lucas.webp";
import rafael from "@/assets/feo/rafael.webp";

const DOCENTE_PREVIEW: string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const product of ["orto", "dor", "esporte", "geronto", "trauma", "prescricao"] as const) {
    for (const p of PROFESSORES_BY_PRODUCT[product]) {
      if (seen.has(p.nome)) continue;
      seen.add(p.nome);
      out.push(p.foto);
      if (out.length >= 9) return out;
    }
  }
  return out;
})();


export const Route = createFileRoute("/quem-somos")({
  head: () => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Quem Somos · Fisio em Ortopedia",
      url: "https://fisioemortopedia.com.br/quem-somos",
      description:
        "Conheça o Portal Fisio em Ortopedia: desde 2019 democratizando o acesso à educação de qualidade em fisioterapia musculoesquelética.",
      about: {
        "@type": "EducationalOrganization",
        name: "Fisio em Ortopedia",
        url: "https://fisioemortopedia.com.br",
        foundingDate: "2019",
        founder: [
          { "@type": "Person", name: "Hélio Nichioka", jobTitle: "Professor e Fisioterapeuta" },
          { "@type": "Person", name: "Leandro Fukusawa", jobTitle: "Professor e Fisioterapeuta" },
          { "@type": "Person", name: "Lucas Nery", jobTitle: "Professor e Fisioterapeuta" },
          { "@type": "Person", name: "Rafael Alaiti", jobTitle: "Professor e Fisioterapeuta" },
        ],
      },
    };
    return {
      meta: [
        { title: "Conheça a Fisio em Ortopedia · Referência em Fisioterapia Musculoesquelética" },
        { name: "description", content: "Desde 2019, quatro fisioterapeutas construíram o maior portal de educação em fisioterapia musculoesquelética do Brasil. Conheça a história, os idealizadores e o que move a FEO." },
        { property: "og:title", content: "Conheça a Fisio em Ortopedia · Referência em Fisioterapia Musculoesquelética" },
        { property: "og:description", content: "De fisioterapeutas para fisioterapeutas: a história dos quatro idealizadores e o que move a FEO desde 2019." },
        { property: "og:url", content: "https://fisioemortopedia.com.br/quem-somos" },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: "Conheça a Fisio em Ortopedia · Referência em Fisioterapia Musculoesquelética" },
        { name: "twitter:description", content: "De fisioterapeutas para fisioterapeutas: a história dos quatro idealizadores e o que move a FEO desde 2019." },

      ],
      links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/quem-somos" }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
    };
  },
  component: QuemSomosPage,
});



const FOUNDERS = [
  { name: "Hélio Nichioka", role: "Professor e Fisioterapeuta", photo: helio },
  { name: "Leandro Fukusawa", role: "Professor e Fisioterapeuta", photo: leandro },
  { name: "Lucas Nery", role: "Professor e Fisioterapeuta", photo: lucas },
  { name: "Rafael Alaiti", role: "Professor e Fisioterapeuta", photo: rafael },
];

function QuemSomosPage() {
  return (
    <div className="min-h-screen bg-white text-[#13123a]">
      <SiteHeader />

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
            <div>
              <p className="text-[var(--brand-soft)] font-semibold uppercase tracking-widest text-xs mb-3">Quem Somos</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                Sobre a Fisio em Ortopedia: de <span className="text-[#5a62ff]">fisioterapeutas</span> para fisioterapeutas
              </h1>


              <div className="mt-8 space-y-5 text-[#13123a]/80 text-base sm:text-lg leading-relaxed">
                <p>
                  Desde 2019, o Portal Fisio em Ortopedia vem democratizando o acesso à educação de qualidade na área da fisioterapia musculoesquelética.
                </p>
                <p>
                  Idealizado como um portal de educação, hoje a Fisio em Ortopedia é mais do que isso: é um portal de inúmeras oportunidades para fisioterapeutas e estudantes da graduação.
                </p>
                <p>
                  Com conteúdos 100% embasados em evidências científicas, o Portal oferece, em parceria com a Faculdade Vanguarda, cinco pós-graduações 100% online, reconhecidas pelo Ministério da Educação e conceituadas no mercado, principalmente pela preocupação em capacitar profissionais qualificados e preparados para atender pacientes com dor.
                </p>
                <p>
                  Acreditando no modelo de aprendizado contínuo, o Portal dispõe, ainda, em seu portfólio, por meio de uma assinatura anual, a <strong>Star Cursos</strong>, a maior biblioteca on-line de cursos da área da fisioterapia musculoesquelética da América Latina. São mais de 70 cursos disponíveis para você aprender no seu ritmo e/ou consultar quando e como quiser.
                </p>
                <p>
                  Além dos produtos apresentados, o Portal tem o comprometimento em oferecer conteúdos gratuitos por meio de suas redes sociais, como também vídeos no Youtube, podcasts, revista com artigos traduzidos, um sistema de busca de artigos para fisioterapeutas e muito mais.
                </p>
                <p className="font-bold text-[#13123a]">
                  Somos o Portal Fisio em Ortopedia.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:sticky lg:top-28">
              <img src={qs1} alt="Atendimento de fisioterapia" className="rounded-2xl w-full h-auto object-cover col-span-2" loading="lazy" />
              <img src={qs2} alt="Estudo em fisioterapia" className="rounded-2xl w-full h-auto object-cover col-span-2" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#13123a] text-white">
        <div className="container mx-auto px-6">
          <h2 className="text-center text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-14">
            Idealizadores do <span className="text-[#95a2ff]">Portal</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {FOUNDERS.map((f) => (
              <div key={f.name} className="text-center">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-white/5">
                  <img src={f.photo} alt={`${f.name}, ${f.role} da Fisio em Ortopedia`} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <h3 className="mt-4 font-bold text-base sm:text-lg uppercase tracking-wide">{f.name}</h3>
                <p className="text-white/60 text-xs sm:text-sm">{f.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BlackDestaqueSection origem="quem-somos" />

      <BlackPopupRolagem origem="quem-somos" />

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="rounded-3xl bg-[#f5f5fb] border border-[#13123a]/10 p-8 sm:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <p className="text-[#3b42d1] font-semibold uppercase tracking-widest text-xs mb-3">
                Corpo docente
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#13123a] leading-tight">
                Mais de 80 professores referência em <span className="text-[#3b42d1]">fisioterapia musculoesquelética</span>
              </h2>
              <p className="mt-5 text-[#13123a]/75 text-base leading-relaxed">
                Especialistas, mestres e doutores que assinam as nossas pós-graduações
                em Orto, Dor, Esporte, Gerontologia, Traumato-ortopedia e Prescrição
                de Exercícios. Veja quem ensina em cada programa.
              </p>
              <div className="mt-6">
                <Link
                  to="/professores"
                  className="inline-flex items-center gap-2 rounded-full bg-[#13123a] text-white font-bold px-6 py-3 hover:bg-[#13123a]/90 transition-colors text-sm"
                >
                  Conheça o corpo docente
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 justify-items-center">
              {DOCENTE_PREVIEW.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Professor ${i + 1} do corpo docente da Fisio em Ortopedia`}
                  loading="lazy"
                  className="h-14 w-14 sm:h-20 sm:w-20 rounded-full object-cover border-2 border-white shadow-sm"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#13123a]">
            Siga nas <span className="text-[#5a62ff]">redes sociais!</span>
          </h2>
          <p className="mt-4 text-[#13123a]/70 text-base sm:text-lg leading-relaxed">
            Para saber mais sobre o nosso time, conteúdo e serviços, acompanhe nossas redes sociais. Instagram, YouTube, podcast no Spotify e muito mais!
          </p>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
}
